import { useEffect, useRef, useState } from "react";

import clsx from "clsx";
import accessibilityIcon from "../../assets/accessibility.png";
import { useAccessibility } from "../core/useAccessibility";

export function AccessibilityPanel() {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);

  const {
    state,
    setFontSize,
    toggleReduceMotion,
    toggleHighlightLinks,
    toggleHighlightHeadings,
    toggleReadableFont,
    setContrastMode,
    reset,
  } = useAccessibility();

  /* ---------------- CLOSE HANDLERS ---------------- */

  // ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Click outside (but ignore clicks on the FAB itself)
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as Node;
      const panel = panelRef.current;
      const btn = buttonRef.current;

      // If click is inside the panel OR on the FAB, do nothing
      if (panel && panel.contains(t)) return;
      if (btn && btn.contains(t)) return;

      setOpen(false);
    };

    // capture=true to be more reliable across nested components
    document.addEventListener("pointerdown", onPointerDown, true);
    return () =>
      document.removeEventListener("pointerdown", onPointerDown, true);
  }, [open]);

  // Focus trap + restore
  useEffect(() => {
    if (!open) return;

    // Save last focused element
    lastFocusedRef.current = document.activeElement as HTMLElement | null;

    const panel = panelRef.current;
    if (!panel) return;

    const focusable = panel.querySelectorAll<HTMLElement>(
      'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])'
    );

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    // Focus first element in panel
    first?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;

      if (focusable.length === 0) return;

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
      } else {
        // Tab
        if (document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };

    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      // Restore focus to FAB
      lastFocusedRef.current?.focus();
    };
  }, [open]);

  /* ---------------- UI ---------------- */

  const btn =
    "w-full px-4 py-4 rounded-xl border text-right font-medium text-base transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500";

  const toggleBtn = (active: boolean) =>
    clsx(
      btn,
      active
        ? "bg-blue-600 text-white border-blue-600"
        : "bg-slate-800 text-slate-200 border-white/10 hover:bg-slate-700"
    );

  const sectionTitle =
    "text-sm font-bold text-slate-400 tracking-wide text-right mt-4 mb-2 uppercase";

  return (
    <>
      {/* Floating Button */}
      <button
        type="button"
        ref={buttonRef}
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "סגירת תפריט נגישות" : "פתיחת תפריט נגישות"}
        aria-expanded={open}
        aria-controls="accessibility-panel"
        className="fixed bottom-5 left-5 z-[9999] w-14 h-14 rounded-full bg-blue-600 shadow-lg flex items-center justify-center hover:scale-105 transition-transform"
      >
        <img
          src={accessibilityIcon}
          alt="אייקון נגישות"
          className="w-7 h-7 object-contain"
          aria-hidden="true"
        />
      </button>

      {/* Backdrop */}
      {open && (
        <div className="fixed inset-0 z-[9998] bg-black/50" aria-hidden />
      )}

      {/* Panel */}
      {open && (
        <div
          ref={panelRef}
          id="accessibility-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="a11y-title"
          aria-describedby="a11y-desc"
          className="
fixed z-[10000]

/* MOBILE (centered) */
left-1/2 top-1/2
-translate-x-1/2 -translate-y-1/2
w-[92vw] max-w-[360px]
max-h-[85dvh]

/* DESKTOP (always inside viewport) */
md:left-5 md:top-12 md:bottom-12
md:translate-x-0 md:translate-y-0
md:w-[360px]
md:max-h-none

rounded-3xl
bg-gradient-to-br from-slate-900 to-slate-950
border border-white/10
backdrop-blur-xl
shadow-2xl shadow-black/60
overflow-hidden flex flex-col
"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10 sticky top-0 bg-slate-900 z-10">
            <h2 id="a11y-title" className="text-xl font-bold text-white">
              נגישות
            </h2>
            <p id="a11y-desc" className="sr-only">
              תפריט נגישות לשינוי גודל טקסט, ניגודיות גבוהה, הדגשת קישורים
              והפחתת תנועה.
            </p>
            <button
              onClick={() => setOpen(false)}
              className="w-10 h-10 rounded-lg border border-white/10 bg-slate-800 text-slate-200 flex items-center justify-center hover:bg-slate-700 focus-visible:ring-2 focus-visible:ring-blue-500"
              aria-label="סגירת תפריט"
            >
              ✕
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3 p-4 overflow-y-auto">
            {/* TEXT */}
            <section className="col-span-2">
              <div className={sectionTitle}>טקסט</div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button
                  onClick={() => setFontSize(state.fontSize + 5)}
                  className={btn}
                >
                  הגדלת טקסט
                </button>
                <button
                  onClick={() => setFontSize(state.fontSize - 5)}
                  className={btn}
                >
                  הקטנת טקסט
                </button>
                <button
                  onClick={toggleReadableFont}
                  aria-pressed={state.readableFont}
                  className={toggleBtn(state.readableFont)}
                >
                  גופן קריא
                </button>
              </div>
            </section>

            {/* CONTRAST */}
            <section className="col-span-2">
              <div className={sectionTitle}>ניגודיות</div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button
                  onClick={() => setContrastMode("dark")}
                  aria-pressed={state.contrastMode === "dark"}
                  className={toggleBtn(state.contrastMode === "dark")}
                >
                  ניגודיות רגילה
                </button>

                <button
                  onClick={() => setContrastMode("high")}
                  aria-pressed={state.contrastMode === "high"}
                  className={toggleBtn(state.contrastMode === "high")}
                >
                  ניגודיות גבוהה
                </button>
              </div>
            </section>

            {/* VISION */}
            <section className="col-span-2">
              <div className={sectionTitle}>ראייה</div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button
                  onClick={toggleHighlightLinks}
                  aria-pressed={state.highlightLinks}
                  className={toggleBtn(state.highlightLinks)}
                >
                  הדגשת קישורים
                </button>
                <button
                  onClick={toggleHighlightHeadings}
                  aria-pressed={state.highlightHeadings}
                  className={toggleBtn(state.highlightHeadings)}
                >
                  הדגשת כותרות
                </button>
              </div>
            </section>

            {/* MOTION */}
            <section className="col-span-2">
              <div className={sectionTitle}>תנועה</div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button
                  onClick={toggleReduceMotion}
                  aria-pressed={state.reduceMotion}
                  className={toggleBtn(state.reduceMotion)}
                >
                  הפחתת אנימציות
                </button>
              </div>
            </section>

            {/* RESET */}
            <section className="col-span-2">
              <button
                onClick={reset}
                className="w-full px-4 py-4 rounded-xl border border-red-500/40 text-red-400 font-bold text-base hover:bg-red-500/10 focus-visible:ring-2 focus-visible:ring-red-500"
              >
                איפוס הגדרות
              </button>
            </section>
          </div>
        </div>
      )}
    </>
  );
}
